import axios, { AxiosProgressEvent } from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Building the PDF report (satellite maps + inset maps) takes 20-60 s on the server
const REPORT_TIMEOUT_MS = 180000;

// Request interceptor to add authentication token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
          
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        window.location.href = '/login';
      }
    }
    
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export type LatLon = [number, number];

/** Plot-to-plot overlap row (overlapping_plots / boundary_touches). New fields optional for old saved results. */
export interface PairRow {
  plot1_index?: number;
  plot1_name?: string;
  plot1_producer?: string;
  plot1_area?: number;
  plot2_index?: number;
  plot2_name?: string;
  plot2_producer?: string;
  plot2_area?: number;
  overlap_area?: number;
  overlap_area_m2?: number;
  overlap_percentage1?: number;
  overlap_percentage2?: number;
  severity?: 'Minor' | 'Moderate' | 'Significant' | 'Below tolerance' | string;
  below_tolerance?: boolean;
  /** Same plot code on two different polygons (a data issue, not neighbouring plots) */
  same_plot_code?: boolean;
  centroid?: LatLon;
}

/** Plot vs reference-layer overlap row (forest reserve / protected area / forest cover). */
export interface RefRow {
  plot_index?: number;
  plot_name?: string;
  name?: string;
  producer_name?: string;
  plot_area_hectares?: number;
  declared_area_hectares?: number | null;
  area?: number | null;
  centroid?: LatLon;
  reference_type?: string;
  reference_name?: string | null;
  reference_code?: string | number | null;
  reference_province?: string | null;
  overlap_hectares?: number;
  overlap_percentage?: number;
  risk_level?: 'Low' | 'Medium' | 'High' | string;
  overlap_centroid?: LatLon;
  // Legacy keys
  forest_overlap_hectares?: number;
  protected_overlap_hectares?: number;
  forest_cover_overlap_hectares?: number;
  FR_NAME?: string;
  NRF_CODE?: string;
  Province?: string;
  name_en?: string;
  name_th?: string;
  pro_id?: string | number;
  reserve_name?: string;
  protected_area_name?: string;
  // Forest cover only
  eudr_risk_level?: string;
  compliance_status?: 'Compliant' | 'Monitor' | 'Requires Review' | string;
}

export interface PlotSummary {
  plot_index: number;
  plot_name: string;
  producer_name?: string;
  declared_area_hectares?: number | null;
  computed_area_hectares?: number;
  area_difference_hectares?: number | null;
  /** (declared − computed) / computed × 100 */
  area_difference_pct?: number | null;
  area_mismatch?: boolean;
  /** Uploaded rows merged into this plot (same code and polygon) */
  merged_rows?: number;
  centroid?: LatLon;
}

export interface AreaCheck {
  tolerance_pct: number;
  plots_checked: number;
  plots_without_declared_area: number;
  plots_outside_tolerance: number;
  note?: string;
}

export interface DuplicateCheck {
  rows_uploaded: number;
  duplicate_rows_merged: number;
  plots_merged?: number;
  codes_with_different_geometries: string[];
}

export interface DatasetInfo {
  name?: string;
  publisher?: string;
  version?: string;
  published?: string;
  source?: string;
  type?: string;
  crs?: string;
  coverage?: string;
  attributes_used?: string;
}

export interface Methodology {
  datasets?: { [key: string]: DatasetInfo };
  area_crs?: string;
  method?: string;
  min_overlap_m2?: number;
  tolerance_note?: string;
  risk_thresholds?: { [category: string]: { [level: string]: string } };
}

export interface AnalysisResults {
  plot_count?: number;
  total_area_hectares?: number;
  total_overlap_count: number;
  overall_overlap_percent: number;
  overlap_percent_forest_reserve: number;
  overlap_percent_protected_area: number;
  overlap_percent_forest_cover?: number | null;
  total_overlap_areas: { [key: string]: number };
  overlapping_plots: PairRow[];
  boundary_touches?: PairRow[];
  forest_overlap_list: RefRow[];
  protected_overlap_list: RefRow[];
  forest_cover_overlap_list: RefRow[];
  forest_cover_no_coverage?: string[];
  plot_summary?: PlotSummary[];
  area_check?: AreaCheck;
  duplicate_check?: DuplicateCheck;
  methodology?: Methodology;
  error_forest_reserve?: string;
  error_protected_area?: string;
  error_forest_cover?: string;
}

export type MapType =
  | 'overlapping-plots'
  | 'protected-area-overlap'
  | 'forest-cover-overlap'
  | 'forest-reserve-overlap'
  | 'all-layers';

export interface UploadSummary {
  total_plots: number;
  total_area_hectares: number;
  unique_producers: number;
  rows_uploaded?: number;
  duplicate_rows_merged?: number;
  codes_with_different_geometries?: string[];
  saved_files: string[];
}

// Authentication interfaces
export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  company_name: string;
  country: string;
  phone?: string;
}

export interface User {
  customer_id: string;
  email: string;
  company_name?: string;
  country?: string;
  credits: number;
  is_active: boolean;
  last_login?: string;
  require_password_change?: boolean;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

// ---------------------------------------------------------------- JSON export

export interface ExportPropertySpec {
  key: string;
  source: string;
  column?: string;
  value?: string;
  date_format?: string;
  decimals?: number;
  fallback?: string;
}

export interface ExportTemplate {
  id?: number | string;
  name: string;
  description?: string;
  container: string;
  properties: ExportPropertySpec[];
  collection_properties?: ExportPropertySpec[];
}

export interface ExportFileInfo {
  name: string;
  rows?: number;
  plots?: number;
  columns?: string[];
  harvest_column?: string | null;
  error?: string;
}

export interface ExportOption {
  value: string;
  label: string;
}

export interface ExportOptions {
  files: ExportFileInfo[];
  uploaded_at: string | null;
  builtin_templates: ExportTemplate[];
  templates: ExportTemplate[];
  sources: ExportOption[];
  collection_sources: string[];
  containers: ExportOption[];
  date_formats: ExportOption[];
  price_credits: number;
}

export interface ExportPreview {
  files: { name: string; output_name: string; rows: number; features: number; warnings: string[] }[];
  sample_file: string;
  sample: unknown;
  price_credits: number;
}

export interface ExportDownload {
  blob: Blob;
  filename: string;
  creditsCharged?: number;
  creditsRemaining?: number;
}

/** FastAPI error detail, also when the response body is a Blob (file downloads). */
async function errorDetail(error: any, fallback: string): Promise<string> {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.detail || parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.detail || data?.message || error?.message || fallback;
}

class ApiService {
  // Authentication methods
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password
      });
      return response.data;
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(userData: RegisterRequest): Promise<any> {
    try {
      const response = await api.post('/api/auth/register', userData);
      return response.data;
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } catch (error: any) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get('/api/auth/profile');
      return response.data;
    } catch (error: any) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  async changePassword(customer_id: string, currentPassword: string, newPassword: string, confirmPassword: string): Promise<any> {
    try {
      const response = await api.put('/api/auth/change-password', {
        old_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  async validateToken(): Promise<any> {
    try {
      const response = await api.get('/api/auth/validate-token');
      return response.data;
    } catch (error: any) {
      console.error('Token validation error:', error);
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<any> {
    try {
      const response = await api.post('/api/auth/forgot-password', {
        email
      });
      return response.data;
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  async resetPassword(email: string, token: string, newPassword: string): Promise<any> {
    try {
      const response = await api.post('/api/auth/reset-password', {
        email,
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  async verifyResetToken(email: string, token: string): Promise<any> {
    try {
      const response = await api.get('/api/auth/verify-reset-token', {
        params: { email, token }
      });
      return response.data;
    } catch (error: any) {
      console.error('Verify reset token error:', error);
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  // Get stored user data
  getStoredUser(): User | null {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Legacy customer ID validation (backward compatibility)
  async validateCustomer(customerId: string): Promise<{ valid: boolean; message: string }> {
    try {
      console.log('Validating customer:', customerId);
      const response = await api.post('/validate-customer/', 
        { customer_id: customerId },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('Validation response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error validating customer:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 
        'Failed to validate customer ID'
      );
    }
  }

  // Upload GeoJSON files
  async uploadGeoJSON(
    customerId: string,
    files: File[], 
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<UploadSummary> {
    try {
      const formData = new FormData();
      formData.append('customer_id', customerId);
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post('/upload-json-api/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      // The new API endpoint returns JSON directly
      return response.data;
    } catch (error: any) {
      console.error('Error uploading GeoJSON:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to upload GeoJSON files'
      );
    }
  }

  // Authenticated upload for logged-in users
  async uploadGeoJSONAuthenticated(
    files: File[], 
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<UploadSummary> {
    try {
      const formData = new FormData();
      
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post('/upload-authenticated/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${this.getToken()}`,
        },
        onUploadProgress,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error uploading files (authenticated):', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message ||
        error.message ||
        'Failed to upload files'
      );
    }
  }

  // Run analysis for authenticated users
  async runAnalysisAuthenticated(): Promise<any> {
    try {
      console.log('Making authenticated API call to /overlap-analysis-authenticated/');
      
      const response = await api.post('/overlap-analysis-authenticated/', {}, {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });
      console.log('Authenticated analysis API response:', response.data);
      return { message: 'Analysis started successfully' };
    } catch (error: any) {
      console.error('Error running authenticated analysis:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message ||
        error.message ||
        'Failed to run analysis'
      );
    }
  }

  // Get analysis results for authenticated users
  async getAnalysisResultsAuthenticated(): Promise<AnalysisResults | null> {
    try {
      console.log('Getting authenticated analysis results...');
      const response = await api.get('/analysis-results-authenticated/', {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`
        }
      });
      console.log('Analysis results response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error getting authenticated analysis results:', error);
      // Return null if no analysis data available
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message ||
        error.message ||
        'Failed to get analysis results'
      );
    }
  }

  // Get analysis results (now requires customer_id for security)
  async getAnalysisResults(customerId: string): Promise<AnalysisResults | null> {
    try {
      const response = await api.get(`/analysis-results/?customer_id=${customerId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error getting analysis results:', error);
      // Return null if no analysis data available
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(
        error.response?.data?.detail || 
        'Failed to get analysis results'
      );
    }
  }

  // Send authenticated email report (for logged-in users)
  async sendEmailAuthenticated(emailData: {
    subject?: string;
    include_report?: boolean;
    include_geojson?: boolean;
  }): Promise<any> {
    try {
      const response = await api.post('/send-email-authenticated/', emailData, {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
        timeout: REPORT_TIMEOUT_MS,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error sending authenticated email:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message ||
        'Failed to send email'
      );
    }
  }

  // Download authenticated report
  async downloadReportAuthenticated(): Promise<Blob> {
    try {
      const response = await api.get('/download-report-authenticated/', {
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
        },
        responseType: 'blob',
        timeout: REPORT_TIMEOUT_MS,
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error downloading authenticated report:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message ||
        'Failed to download report'
      );
    }
  }

  // Send email report (now requires customer_id for security)
  async sendEmail(customerId: string, emailData: {
    recipient: string;
    subject?: string;
    include_report?: boolean;
    include_geojson?: boolean;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('customer_id', customerId);
      formData.append('recipient', emailData.recipient);
      formData.append('subject', emailData.subject || 'EUDR Compliance Analysis Results');
      formData.append('include_report', emailData.include_report ? 'true' : 'false');
      formData.append('include_geojson', emailData.include_geojson ? 'true' : 'false');

      const response = await api.post('/send-email/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Handle different response formats
      const data = response.data;
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to send email');
      }
      
      return data;
    } catch (error: any) {
      console.error('Error sending email:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message ||
        'Failed to send email'
      );
    }
  }

  // Credit validation
  async validateCredits(customerId: string): Promise<{ valid: boolean; current_credits: number; required_credits: number; message?: string }> {
    try {
      console.log('Validating credits for customer:', customerId);
      const response = await api.post(`/api/credits/validate/${customerId}`);
      return {
        valid: response.data.data.valid,
        current_credits: response.data.data.current_credits,
        required_credits: response.data.data.required_credits
      };
    } catch (error: any) {
      console.error('Error validating credits:', error);
      if (error.response?.status === 402) {
        return {
          valid: false,
          current_credits: 0,
          required_credits: 50,
          message: error.response.data.message
        };
      }
      throw new Error(
        error.response?.data?.detail || 
        'Failed to validate credits'
      );
    }
  }

  // Get credit balance
  async getCreditBalance(customerId: string): Promise<any> {
    try {
      const response = await api.get(`/api/credits/balance/${customerId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error getting credit balance:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to get credit balance'
      );
    }
  }

  // Purchase credits (test mode)
  async purchaseCredits(customerId: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('customer_id', customerId);
      formData.append('payment_reference', `test_purchase_${Date.now()}`);

      const response = await api.post('/api/credits/purchase', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error purchasing credits:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to purchase credits'
      );
    }
  }

  // Add free signup credits (30 credits)
  async addSignupBonus(customerId: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('customer_id', customerId);

      const response = await api.post('/api/credits/signup-bonus', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error adding signup bonus:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to add signup bonus'
      );
    }
  }

  // Create customer account
  async createCustomer(customerData: {
    customer_id: string;
    email: string;
    company_name: string;
    country: string;
    phone?: string;
  }): Promise<any> {
    try {
      const response = await api.post('/api/customers/create', customerData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating customer:', error);
      throw error; // Re-throw to preserve error details for signup form
    }
  }

  // Email verification methods
  async sendVerificationEmail(customerData: {
    email: string;
    company_name: string;
    country: string;
    phone?: string;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('email', customerData.email);
      formData.append('company_name', customerData.company_name);
      formData.append('country', customerData.country);
      if (customerData.phone) {
        formData.append('phone', customerData.phone);
      }

      const response = await api.post('/api/verification/send-verification', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to send verification email'
      );
    }
  }

  async verifyEmail(email: string, token: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('token', token);

      const response = await api.post('/api/verification/verify-email', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error verifying email:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to verify email'
      );
    }
  }

  async resendVerification(email: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await api.post('/api/verification/resend-verification', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error resending verification:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to resend verification'
      );
    }
  }

  async checkVerificationStatus(email: string): Promise<any> {
    try {
      const response = await api.get(`/api/verification/check-status/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      console.error('Error checking verification status:', error);
      throw new Error(
        error.response?.data?.detail || 
        'Failed to check verification status'
      );
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    try {
      const response = await api.get('/');
      return { status: 'ok' };
    } catch (error) {
      console.error('Error checking API health:', error);
      throw new Error('API is not available');
    }
  }

  // Get map visualization data (now requires customer_id for security)
  async getMapData(customerId: string, mapType: MapType, focus?: string): Promise<string> {
    try {
      let url = `/map/${mapType}/?customer_id=${encodeURIComponent(customerId)}`;
      if (focus) {
        url += `&focus=${encodeURIComponent(focus)}`;
      }
      const response = await api.get(url);
      return response.data; // Returns HTML content with embedded map data
    } catch (error: any) {
      console.error(`Error getting ${mapType} map data:`, error);
      if (error.response?.status === 400) {
        throw new Error(`No analysis data found for customer ${customerId}. Upload and analyze files first.`);
      }
      throw new Error(
        error.response?.data?.detail || 
        `Failed to get ${mapType} map data`
      );
    }
  }

  // ---------------------------------------------------------------- JSON export

  async getExportOptions(): Promise<ExportOptions> {
    try {
      const response = await api.get('/export/options', { timeout: 120000 });
      return response.data;
    } catch (error: any) {
      throw new Error(await errorDetail(error, 'Failed to load export options'));
    }
  }

  async saveExportTemplate(template: ExportTemplate): Promise<ExportTemplate> {
    const { id, ...body } = template;
    try {
      const response = typeof id === 'number'
        ? await api.put(`/export/templates/${id}`, { template: body })
        : await api.post('/export/templates', { template: body });
      return response.data;
    } catch (error: any) {
      throw new Error(await errorDetail(error, 'Failed to save template'));
    }
  }

  async deleteExportTemplate(id: number): Promise<void> {
    try {
      await api.delete(`/export/templates/${id}`);
    } catch (error: any) {
      throw new Error(await errorDetail(error, 'Failed to delete template'));
    }
  }

  async previewExport(template: ExportTemplate, files: string[]): Promise<ExportPreview> {
    const { id, ...body } = template;
    try {
      const response = await api.post('/export/preview', { template: body, files }, { timeout: 120000 });
      return response.data;
    } catch (error: any) {
      throw new Error(await errorDetail(error, 'Failed to build the preview'));
    }
  }

  async downloadExport(template: ExportTemplate, files: string[]): Promise<ExportDownload> {
    const { id, ...body } = template;
    try {
      const response = await api.post('/export/download', { template: body, files }, {
        responseType: 'blob',
        timeout: 180000,
      });
      const disposition: string = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const charged = response.headers['x-credits-charged'];
      const remaining = response.headers['x-credits-remaining'];
      return {
        blob: response.data,
        filename: match ? match[1] : (files.length === 1 ? 'export.geojson' : 'export.zip'),
        creditsCharged: charged !== undefined ? Number(charged) : undefined,
        creditsRemaining: remaining !== undefined ? Number(remaining) : undefined,
      };
    } catch (error: any) {
      throw new Error(await errorDetail(error, 'Export failed'));
    }
  }
}

export const apiService = new ApiService();
export default apiService;
